import {AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {Router} from "@angular/router";
import {DropzoneComponent} from "../../dropzone/dropzone.component";
import {AuthorityPreviewComponent} from "../authority-preview/authority-preview.component";
import {SignaturePadComponent} from "../../signature-pad/signature-pad.component";
import {AuthorityData} from "../../../model/authorityData";
import {SubauthorityPreviewComponent} from "../subauthority-preview/subauthority-preview.component";
import {SubauthorityData} from "../../../model/subauthorityData";
import {DownloadService} from "../../../service/download.service";
import {faArrowLeft, faInfo, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import * as Sentry from "@sentry/angular";


/**
 * Overview over authorities and legitimate interests in the order process
 * only shows when the PlatformProfile is not the connect platform
 */
@Component({
  selector: 'app-authority-page',
  templateUrl: './authority-page.component.html',
  styleUrls: ['./authority-page.component.css']
})

export class AuthorityPageComponent implements OnInit, AfterViewInit {

  @ViewChild('authorityDropzone') authorityDropzoneComponent: DropzoneComponent;
  @ViewChild('subauthorityDropzone') subauthoritDropzoneComponent: DropzoneComponent;
  @ViewChild(AuthorityPreviewComponent) authorityPreview: AuthorityPreviewComponent;
  @ViewChild(SubauthorityPreviewComponent) subauthorityPreview: SubauthorityPreviewComponent;
  @ViewChild(SignaturePadComponent) signaturePad: SignaturePadComponent;

  errorMsg: string;
  legitimateInterest: string = "init";
  authorityData: AuthorityData;
  subAuthorityData : SubauthorityData;

  showAuthorityModal: boolean = false;
  showSubAuthorityModal: boolean = false;
  uploadedOwnAuthority: boolean = false;
  uploadedOwnSubauthority: boolean = false;
  displayNachweisDrop: boolean = false;
  uploadingAuthorities: boolean = false;

  mobile: boolean = false;
  isTaxPlatform: boolean = false;

  constructor(private router: Router,
              private downloadService:DownloadService,
              private cd: ChangeDetectorRef
  ) {
    if (sessionStorage.getItem("new_order_properties") === null) {
      this.router.navigate(['/new_order']);
    }
  }

  ngOnInit(): void {
    if (window.screen.width <= 1000) { // 768px portrait
      this.mobile = true;
    }
    this.setAuthorityDataFromSession();
    this.setSubAuthorityDataFromSession();

    let platformProfile = JSON.parse(sessionStorage.getItem("platform"))
    if(platformProfile.platformType === "STEUER"){
      this.isTaxPlatform = true;
    }

    let interest = sessionStorage.getItem("interest")

    if(interest){
      this.legitimateInterest = interest;
      this.displayNachweis();
      this.cd.detectChanges();
    }

  }

  ngAfterViewInit(): void {
    this.displayNachweis();
    this.cd.detectChanges();
  }

  displayNachweis() {
    setTimeout(() => {
      this.displayNachweisDrop = this.legitimateInterest === "owner";
    })

  }

  /**
   * If the user wants to go to the next page this function is called.
   * Checks if all criterias are met and displays a warning if not.
   */
  displayWarningMessage(): void {
    this.uploadedOwnAuthority = this.authorityDropzoneComponent.files.length > 0;
    this.uploadedOwnSubauthority = this.subauthoritDropzoneComponent.files.length > 0;

    if (this.legitimateInterest === "init") {
      this.errorMsg = "Bitte geben Sie ein Berechtigtes Interesse an."
    } else if (!this.validateAuthorityData() && this.uploadedOwnAuthority === false && !this.hasGeneralAuthority()) {
      this.errorMsg = "Bitte signieren Sie die Vollmacht."
    } else if (!this.validateSubAuthorityData() && this.uploadedOwnSubauthority === false && !this.displayNachweisDrop) {
      this.errorMsg = "Bitte signieren Sie die Untervollmacht."
    } else {
      const authorityPromise = this.authorityDropzoneComponent.uploadFilesPromise('authority');
      const subAuthorityPromise = this.subauthoritDropzoneComponent.uploadFilesPromise('subauthority');
      this.handleFileUploadsAndNavigateToSummary.call(this, authorityPromise, subAuthorityPromise);
    }
  }

  /**
   * Makes sure the authorities are properly uploaded before the user is sent to the summary page
   *
   * @param authorityPromise The authorityPromise containing the request for the authority
   * @param subAuthorityPromise The subAuthorityPromise containing the request for the subAuthority
   */
  handleFileUploadsAndNavigateToSummary(authorityPromise: any, subAuthorityPromise: any): void {
    this.uploadingAuthorities = true;
    Promise.all([authorityPromise, subAuthorityPromise])
        .then(results => {
          const [authorityResult, subAuthorityResult] = results;
          if (authorityResult && subAuthorityResult) {
            sessionStorage.setItem("interest", this.legitimateInterest);
            this.uploadingAuthorities = false;
            this.router.navigate(["/cart"]);
          } else {
            this.uploadingAuthorities = false;
            this.errorMsg = "Beim Hochladen der Vollmachten ist ein Fehler aufgetreten! Bitte versuchen sie es erneut."
          }
        })
        .catch(error => {
          Sentry.captureException(error);
          console.error("Error:", error);
          this.uploadingAuthorities = false;
        });
  }

  validateAuthorityData(): boolean {
    let data = this.getAuthorityDataFromSession();
    if (data != null) {
      return (!!(data.signingDate && data.signingUrl && data.person && data.company))
    }
    return false;
  }

  validateSubAuthorityData(): boolean{
    let data = this.getSubAuthorityDataFromSession();
    if(data != null){
      return(!!(data.signingDate && data.signingUrl && data.date && data.gesellschaft && data.company && data.person))
    }
    return false;
  }

  setAuthorityDataFromSession(): void{
    this.authorityData = this.getAuthorityDataFromSession();
  }

  setSubAuthorityDataFromSession():void{
    this.subAuthorityData = this.getSubAuthorityDataFromSession();
  }

  getAuthorityDataFromSession(): AuthorityData {
    let ob = sessionStorage.getItem("authorityData");
    if (ob != null)
      return JSON.parse(ob);
    return null;
  }

  getSubAuthorityDataFromSession(): SubauthorityData{
    let ob = sessionStorage.getItem("subAuthorityData");
    if (ob != null)
      return JSON.parse(ob);
    return null;
  }

  hasGeneralAuthority(){
    let platformProfile = JSON.parse(sessionStorage.getItem("platform"));
    if(platformProfile !== null){
      let generalAuthorities = platformProfile.authorities;
      return generalAuthorities != null && generalAuthorities.length > 0;
    }
  }

  downloadSubAuthority(subAuthorityData){
      this.downloadService.downloadSubAuthority(subAuthorityData)
  }

  downloadAuthority(authorityData){
    this.downloadService.downloadAuthority(authorityData);
  }

  /**
   * Download a PDF authority template for a specific authority type depending on the selected legitimateInterest
   */
  downloadAuthorityPdfTemplate(){
    const authorityType = this.legitimateInterest === "owner" ? "DIRECT" : null
    this.downloadService.downloadAuthorityPdfTemplate(authorityType);
  }

  protected readonly faArrowLeft = faArrowLeft;
  protected readonly faInfo = faInfo;
  protected readonly faInfoCircle = faInfoCircle;
}
